import { US_CITIZEN } from "@equidefi/constants/profile";
import { latestInvestorPayment, userFullName } from "@equidefi/shared";
import { EXEMPTION_TYPE_DISPLAY, INVESTOR_TYPE_DISPLAY } from "@equidefi/shared/constants/offerings";
import type { DTO } from "@equidefi/shared/types";
import { useMemo } from "react";

interface IntercomMetadata {
  // Investment data
  investment_id?: string;
  investment_amount?: number;
  paid_amount?: number;
  workflow_step?: string;
  workflow_status?: string;
  investor_type?: string;
  total_signers?: number;
  investment_create_date?: Date;

  // Offering data
  offering_id?: string;
  offering_name?: string;
  offering_regulation?: string;
  issuer_name?: string;

  // User context
  name?: string;
  email?: string;
  phone?: string;
  user_id?: string;
  entity_type?: string;
  account_type?: string;
  company?: string;

  // Address information
  us_citizen?: boolean;
  address_street_1?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  address_country?: string;

  // Payment data
  most_recent_payment_method?: string;
}

/**
 * Hook to collect investment metadata for Intercom user identification
 */
export const useInvestmentMetadataForIntercom = (
  user?: DTO.User,
  investment?: DTO.Investment,
  offering?: DTO.Offering,
  issuer?: DTO.Issuer,
): IntercomMetadata => {
  return useMemo(() => {
    // Return empty object if essential data is missing to avoid undefined issues
    if (!user || !investment || !offering) {
      return {};
    }

    // Helper function to safely extract values and handle null/undefined
    const safeValue = (value: any) => {
      // Intercom API rejects null values. Every null value must be replaced with undefined
      if (!value || value === null) return undefined;
      return value;
    };

    try {
      const { vault } = investment;

      const isUSCitizen = vault?.citizenship ? (vault?.citizenship === US_CITIZEN ? "Yes" : "No") : undefined;

      return {
        // Investment data
        investment_id: safeValue(investment?.id),
        investment_amount: safeValue(investment?.amount),
        paid_amount: safeValue(investment?.paid_amount),
        workflow_step: safeValue(investment?.next),
        workflow_status: safeValue(investment?.status),
        investor_type: safeValue(vault?.ta_account_type && INVESTOR_TYPE_DISPLAY[vault.ta_account_type]),
        total_signers: safeValue(investment?.signer_count),
        investment_create_date: safeValue(investment?.create_date),

        // Offering data
        offering_id: safeValue(offering?.id),
        offering_name: safeValue(offering?.name),
        offering_regulation: safeValue(offering?.exemption_type && EXEMPTION_TYPE_DISPLAY[offering.exemption_type]),
        issuer_name: safeValue(offering?.issuer?.name || issuer?.name),

        // User context
        name: safeValue(vault && userFullName(vault)),
        email: safeValue(user.email),
        phone: safeValue(vault?.phone_number),
        user_id: safeValue(user?.id),
        entity_type: safeValue(vault?.entity_type),
        account_type: safeValue(vault?.ta_account_type),
        company: safeValue(vault?.company),

        // Address information
        us_citizen: safeValue(isUSCitizen),
        address_street_1: safeValue(vault?.address_street_1),
        address_city: safeValue(vault?.address_city),
        address_state: safeValue(vault?.address_state),
        address_postal_code: safeValue(vault?.address_postal_code),
        address_country: safeValue(vault?.address_country_code),

        most_recent_payment_method: safeValue(latestInvestorPayment(investment?.payments)?.payment_method_display),
      };
    } catch (error) {
      console.error("Error collecting Intercom metadata:", error);
      return {};
    }
  }, [user, investment, offering]);
};
